'use client';

import { useEffect } from 'react';

// Local origins where HMR should work (dev server is running locally)
const LOCAL_ORIGINS = [
  'localhost',
  '127.0.0.1',
];

/**
 * Component that disables Hot Module Replacement (HMR) when:
 * 1. NEXT_PUBLIC_DISABLE_HMR environment variable is set to 'true'
 * 2. The app is accessed from an external domain (not localhost)
 *    because HMR WebSocket cannot connect to external domain without proxy
 *
 * This prevents 404 errors on webpack-hmr and stops automatic page refreshes
 * when running npm run dev but accessing via external domain like app-test.innovanet.com
 */
export function HMRDisabler() {
  useEffect(() => {
    // Only run in development mode
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    const disableHMR = process.env.NEXT_PUBLIC_DISABLE_HMR === 'true';
    
    // Check if current hostname is local (where HMR can work)
    const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalOrigin = LOCAL_ORIGINS.some(origin =>
      currentHostname === origin || currentHostname.includes(origin)
    );

    // Don't disable HMR if we're on localhost
    if (isLocalOrigin && !disableHMR) {
      return;
    }

    // Disable HMR for external domains (no proxy to local dev server)
    const isExternalDomain = !isLocalOrigin;
    
    if (disableHMR || isExternalDomain) {
      // Disable the WebSocket connection for HMR
      // This prevents the 404 errors and reconnection loops
      if (typeof window !== 'undefined') {
        // Override WebSocket to block HMR connections
        const OriginalWebSocket = window.WebSocket;
        
        // @ts-ignore - We're intentionally overriding WebSocket
        window.WebSocket = function(url: string, protocols?: string | string[]) {
          // Block HMR WebSocket connections
          if (url.includes('_next/webpack-hmr') ||
              url.includes('/_next/webpack') ||
              url.includes('__webpack_hmr')) {
            // Return a mock WebSocket that does nothing
            return {
              close: () => {},
              send: () => {},
              addEventListener: () => {},
              removeEventListener: () => {},
              readyState: 3, // CLOSED
              CONNECTING: 0,
              OPEN: 1,
              CLOSING: 2,
              CLOSED: 3,
              binaryType: 'blob',
              bufferedAmount: 0,
              extensions: '',
              onclose: null,
              onerror: null,
              onmessage: null,
              onopen: null,
              protocol: '',
              url: url,
            } as unknown as WebSocket;
          }
          
          // Allow other WebSocket connections
          return new OriginalWebSocket(url, protocols);
        } as typeof WebSocket;
        
        // Copy static properties
        // @ts-ignore
        window.WebSocket.CONNECTING = OriginalWebSocket.CONNECTING;
        // @ts-ignore
        window.WebSocket.OPEN = OriginalWebSocket.OPEN;
        // @ts-ignore
        window.WebSocket.CLOSING = OriginalWebSocket.CLOSING;
        // @ts-ignore
        window.WebSocket.CLOSED = OriginalWebSocket.CLOSED;
        // @ts-ignore
        window.WebSocket.prototype = OriginalWebSocket.prototype;

        // Also block EventSource (used by some HMR implementations)
        const OriginalEventSource = window.EventSource;
        if (OriginalEventSource) {
          // @ts-ignore
          window.EventSource = function(url: string, eventSourceInitDict?: EventSourceInit) {
            if (url.includes('_next/webpack-hmr') ||
                url.includes('/_next/webpack') ||
                url.includes('__webpack_hmr')) {
              // Return a mock EventSource that does nothing
              return {
                close: () => {},
                addEventListener: () => {},
                removeEventListener: () => {},
                readyState: 2, // CLOSED
                CONNECTING: 0,
                OPEN: 1,
                CLOSED: 2,
                onerror: null,
                onmessage: null,
                onopen: null,
                url: url,
                withCredentials: false,
              } as unknown as EventSource;
            }
            
            return new OriginalEventSource(url, eventSourceInitDict);
          } as typeof EventSource;
          
          // @ts-ignore
          window.EventSource.CONNECTING = OriginalEventSource.CONNECTING;
          // @ts-ignore
          window.EventSource.OPEN = OriginalEventSource.OPEN;
          // @ts-ignore
          window.EventSource.CLOSED = OriginalEventSource.CLOSED;
        }
      }
    }
  }, []);

  return null;
}
